<template>
    <app-layout title="Edit Order">
        <form @submit.prevent="handleSubmit">
            <page-header>
                <template #title>Edit Order</template>

                <template #actions>
                    <jet-button v-show="form.isDirty" type="submit" class="leading-none" :disabled="form.processing" >
                        Save
                    </jet-button>

                    <jet-tertiary-button
                        v-if="form.isDirty"
                        :disabled="form.processing"
                        class="leading-none"
                        @click="showUnsavedChangesModal = true"
                    >
                        Cancel
                    </jet-tertiary-button>

                    <jet-tertiary-button
                        v-else
                        :disabled="form.processing"
                        :href="backUrl"
                        class="leading-none"
                    >
                        Close
                    </jet-tertiary-button>
                </template>
            </page-header>

            <customer-form
                v-if="order.is_wholesale"
                :customers="customers"
                :categories="categories"
                :holidays="holidays"
                :errors="errors"
                :form="form"
                class="px-3 md:px-4 lg:px-8"
            />
            <div v-else class="flex flex-col px-3 md:px-4 lg:px-8 gap-y-4 ">
                <order-details
                    :order="order"
                    :key="order.status"
                />
            </div>

            <div v-if="order.status != 2 && order.payment_status == 'paid'" class="px-3 md:px-4 lg:px-8 py-4 text-red-600 font-bold">
                This order is already paid for. Please manually refund for any order changes as necessary
            </div>

            <div class="px-3 md:px-4 lg:px-8 py-4">
                <order-items-edit
                    :order="order"
                    :form="form"
                    :customers="customers"
                    :categories="categories"
                    :is_wholesale="order.is_wholesale"
                    :orderMinimum="orderMinimum"
                    :deliveryFeeBelowMinimum="deliveryFeeBelowMinimum"
                    :manualPickingMode="manualPickingMode"
                    @updateCanPlaceOrder="updateCanPlaceOrder"
                />
            </div>

            <div class="text-center py-24" >
                <p v-if="order.status === -1" class="text-2xl text-red-700 opacity-50">--- Cancelled ---</p>

                <div v-else class="flex justify-center gap-x-8">
                    <jet-danger-button v-if="order.status === 0" @click="showCancelModal = true">
                        Cancel order
                    </jet-danger-button>
                </div>

            </div>
        </form>

        <unsaved-changes-modal-2
            :formRoute="formRoute"
            :isDirty="form.isDirty"
            :show="showUnsavedChangesModal"
            @close="showUnsavedChangesModal = false"
            @confirm="$inertia.get(backUrl)"
        />

        <jet-confirmation-modal :show="showCancelModal" @close="showCancelModal=false">
            <template #title>
                Cancel Order
            </template>

            <template #content>
                Are you sure you would like to cancel this order?
            </template>

            <template #footer>
                <jet-danger-button @click="cancelOrder" :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                    Yes
                </jet-danger-button>

                <jet-tertiary-button class="ml-2" @click="showCancelModal=false">
                    No
                </jet-tertiary-button>
            </template>
        </jet-confirmation-modal>

        <jet-dialog-modal :show="showInvoicedModal" @close="showInvoicedModal=false">
            <template #title>
                Mark Order Invoiced
            </template>

            <template #content>
                Are you sure you would like to mark this order invoiced?
            </template>

            <template #footer>
                <jet-button @click="markOrderInvoiced">
                    Yes
                </jet-button>

                <jet-tertiary-button class="ml-2" @click="showInvoicedModal=false">
                    No
                </jet-tertiary-button>
            </template>
        </jet-dialog-modal>

    </app-layout>
</template>

<script>
import { useForm } from '@inertiajs/inertia-vue3';
import { DateTime } from "luxon";
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import OrderDetails from '@/Shared/Orders/OrderDetails.vue';
import CustomerForm from '@/Pages/Orders/Partials/CustomerForm.vue';
import OrderItemsEdit from '@/Shared/Orders/OrderItemsEdit';
import JetButton from '@/Jetstream/Button';
import JetDialogModal from '@/Jetstream/DialogModal';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import UnsavedChangesModal2 from '@/Shared/UnsavedChangesModal2';

export default {
    setup(props) {
        const form = useForm({
            id: props.order.id,
            allow_substitutions: props.order.allow_substitutions,
            order_date: new Date(props.order.order_date + ' EST'),
            ship_date: new Date(props.order.ship_date + ' EST'),
            shipping_method: props.order.shipping_method,
            payment_status: props.order.payment_status,
            override_ship_date: props.order.override_ship_date,
            customer_id: props.order.customer_id,
            delivery_fee: props.order.delivery_fee,
            applied_credit_amount: props.order.applied_credit_amount,
            notes: props.order.notes,
            is_pricing_locked: props.order.is_pricing_locked,
            is_admin: props.is_admin,
            override_stock_restrictions: false,
            order_items: props.order.order_items.map(i => {
                return {
                    id: i.id,
                    order_id: i.order_id,
                    product_id: i.product_id,
                    sfd_product: i.sfd_product,
                    substitution: i.substitution,
                    title: i.title,
                    description: i.description,
                    product_notes: i.product_notes,
                    size: i.size,
                    tax: i.tax,
                    partial_qty: i.partial_qty,
                    cost: i.cost,
                    price: i.price,
                    quantity: i.quantity,
                    orig_quantity: i.orig_quantity,
                    product: i.product,
                    zone: i.zone,
                }
            }),
        })

        return { form }
    },

    components: {
        AppLayout,
        PageHeader,
        OrderDetails,
        CustomerForm,
        OrderItemsEdit,
        JetButton,
        JetDangerButton,
        JetDialogModal,
        JetTertiaryButton,
        JetConfirmationModal,
        UnsavedChangesModal2,
    },

    props: ['order', 'customers', 'categories', 'holidays', 'errors', 'orderMinimum', 'deliveryFeeBelowMinimum', 'is_admin', 'manualPickingMode'],

    data() {
        return {
            formRoute: this.route('orders.update', this.order.id),
            canPlaceOrder: false,
            showCancelModal: false,
            showInvoicedModal: false,
            showUnsavedChangesModal: false,
        }
    },

    methods: {
        handleSubmit () {
            if(!this.form.processing) {
                const returnUrl = route().params.return;

                this.form
                    .transform((data) => ({
                        ...data,
                        order_date: data.order_date ? this.formatDate(data.order_date) : null,
                        ship_date: data.ship_date ? this.formatDate(data.ship_date) : null,
                        manualPickingMode: this.manualPickingMode,
                    }))
                    .submit('put', route('orders.update', {
                        order: this.order.id,
                        return: returnUrl,
                    }));
            }
        },

        formatDate (date) {
            // Takes a Javascript date object and returns it in formay 'YYYY-MM-DD'
            return date.toISOString().split('T')[0];
        },

        cancelOrder () {
            if(!this.form.processing) this.form.delete(route('orders.destroy', this.order.id));
        },
        updateCanPlaceOrder(value) {
            this.canPlaceOrder = value;
        },

    },

    computed: {
        backUrl () {
            const params = route().params;

            if (params.return) {
                return params.return;
            }

            return route('orders.index', {
                page: this.$page.props.page.orders,
            });
        },
    },
}
</script>

<style>

</style>
